import { ArrowLeft, PlugZap, RotateCw, SearchX, ShieldX, TriangleAlert } from "lucide-react";
import { type ReactNode, useEffect, useState } from "react";

import { Button } from "@/components/ui/button";
import { errorMessage, isUnreachable } from "@/services/api";

/**
 * A whole page when something has gone wrong, rather than a sentence.
 *
 * The shared frame: an icon, what happened, what to do about it. Both the
 * error and the not-found pages use it so a dead end looks like part of the
 * application instead of the browser's own failure.
 */
function Stop({
  icon,
  eyebrow,
  title,
  children,
  actions,
  detail,
}: {
  icon: ReactNode;
  eyebrow: string;
  title: string;
  children: ReactNode;
  actions: ReactNode;
  /** Only rendered in development: the stack, for whoever is looking. */
  detail?: string | undefined;
}) {
  return (
    <div className="grid min-h-dvh place-items-center px-6 py-12">
      <div className="w-full max-w-lg text-center">
        <span className="bg-surface-inset text-fg-muted mx-auto grid size-14 place-items-center rounded-2xl">
          {icon}
        </span>
        <p className="text-eyebrow text-fg-subtle mt-5 tracking-[0.04em] uppercase">
          {eyebrow}
        </p>
        <h1 className="text-title-1 text-heading mt-2">{title}</h1>
        <div className="text-fg-muted text-callout mt-3 text-pretty">{children}</div>
        <div className="mt-7 flex flex-wrap justify-center gap-2.5">{actions}</div>

        {detail ? (
          <details className="border-line bg-surface-raised mt-8 rounded-xl border text-left">
            <summary className="text-fg-muted text-subhead cursor-pointer px-4 py-3">
              Technical detail (development only)
            </summary>
            <pre className="text-footnote text-fg-muted max-h-64 overflow-auto px-4 pb-4 whitespace-pre-wrap">
              {detail}
            </pre>
          </details>
        ) : null}
      </div>
    </div>
  );
}

/**
 * What a thrown error looks like.
 *
 * The message is the one the API sent where there is one — an RFC 9457 detail
 * says what actually happened — and a plain sentence otherwise. "Something went
 * wrong" is what a page says when nobody decided what it should say.
 *
 * The stack is shown in development only: in production it tells the reader
 * nothing and tells anyone else too much.
 */
export function ErrorPage({
  error,
  reset,
}: {
  error: Error;
  reset?: (() => void) | undefined;
}) {
  // A server that did not answer is not a page that went wrong. It is the
  // whole application waiting, it clears by itself, and there is nothing for
  // anyone to do but come back — which is a different screen and a different
  // sentence. This is every route's error boundary, so putting the branch here
  // covers a cold start as well as a page already open.
  if (isUnreachable(error)) return <ServerDown />;

  // The API's own words where there are any, the thrown message otherwise, and
  // a plain sentence only when there is genuinely nothing to say. A page that
  // knows what went wrong should say it.
  const fallback =
    "The page could not be shown. The details were logged for whoever looks after this.";
  const message = errorMessage(error, error.message?.trim() || fallback);
  return (
    <Stop
      icon={<TriangleAlert className="text-danger size-7" aria-hidden />}
      eyebrow="Error"
      title="This page could not be shown"
      detail={import.meta.env.DEV ? (error.stack ?? String(error)) : undefined}
      actions={
        <>
          {reset ? (
            <Button onClick={reset}>
              <RotateCw aria-hidden /> Try again
            </Button>
          ) : null}
          <Button variant="secondary" onClick={() => window.location.reload()}>
            <RotateCw aria-hidden /> Reload the page
          </Button>
          <Button variant="ghost" asChild>
            <a href="/">
              <ArrowLeft aria-hidden /> Back to the dashboard
            </a>
          </Button>
        </>
      }
    >
      <p>{message}</p>
      <p className="mt-2">
        Nothing has happened to your session — you are still signed in, and no work has been
        lost.
      </p>
    </Stop>
  );
}

/** How long the retry stays shut after a press that found nothing. Long enough
 *  for a restart to finish, so the press that is allowed is a press with a
 *  chance of working. */
const RETRY_SECONDS = 30;

/** The liveness probe sits outside the versioned prefix, so it is not on the
 *  API client's base URL. */
const HEALTH_URL = `${import.meta.env.VITE_API_GENESIS_URL ?? ""}/health`;

/**
 * The server is not answering.
 *
 * One action, pressed by a person. Nothing is polled in the background: a page
 * that asks on its own is every open tab asking at once, for as long as the
 * outage lasts, of a server that is already struggling — and the one moment
 * anybody cares about the answer is the moment they press the button.
 *
 * The press asks `/health` rather than replaying the route, so "is anything
 * listening" is one small request instead of the whole page's worth. A plain
 * `fetch` because the probe needs no token: the API client would attach a
 * bearer and try to refresh it against a server that is not there. Only when
 * it answers does the route run again.
 *
 * A press that finds nothing shuts the button for another half minute, so the
 * button cannot become the hammering the background poll was removed for.
 */
function ServerDown() {
  const [seconds, setSeconds] = useState(RETRY_SECONDS);
  const [checking, setChecking] = useState(false);
  /** A press has come back with the server still down — said out loud, because
   *  a button that shuts itself again and explains nothing looks broken. */
  const [stillDown, setStillDown] = useState(false);

  useEffect(() => {
    if (seconds === 0) return;
    const id = setTimeout(() => setSeconds((left) => left - 1), 1000);
    return () => clearTimeout(id);
  }, [seconds]);

  const tryAgain = async () => {
    setChecking(true);
    setStillDown(false);
    try {
      const answer = await fetch(HEALTH_URL, { cache: "no-store" });
      if (answer.ok) {
        // A reload, not the boundary's `reset`. `reset` clears the React error
        // boundary, but the router's loader has already rejected and that
        // rejection is what is cached — so the route re-renders straight back
        // into this screen with `/health` sitting at 200 in the network panel.
        // Reloading re-runs everything from nothing, which also throws away the
        // client's shut door for free. There is no state here worth keeping:
        // the page it would preserve is this one.
        window.location.reload();
        return;
      }
    } catch {
      // No answer at all, which is the same news as a bad one here.
    }
    setChecking(false);
    setStillDown(true);
    setSeconds(RETRY_SECONDS);
  };

  return (
    <Stop
      icon={<PlugZap className="size-7" aria-hidden />}
      eyebrow="Server unavailable"
      title="Waiting for the server"
      actions={
        <Button
          disabled={seconds > 0 || checking}
          loading={checking}
          onClick={() => void tryAgain()}
        >
          <RotateCw aria-hidden />
          {seconds > 0 ? `Try again in ${seconds}s` : "Try again"}
        </Button>
      }
    >
      <p>
        The server is not answering. It may be restarting or briefly unreachable — this is not
        something wrong with your account, and nothing you have done has been lost.
      </p>
      <p className="mt-2" aria-live="polite">
        {stillDown
          ? "Still not answering. The button opens again shortly."
          : "Try again when you are ready — the check is quick."}
      </p>
    </Stop>
  );
}

/**
 * A route this account may not open.
 *
 * Distinct from 404 on purpose: the page exists, and saying so is not a leak —
 * the navigation already names the screens. What it must not do is pretend the
 * address is wrong and send someone hunting for a typo.
 */
export function UnauthorizedPage({ title = "You do not have access to this page" }) {
  return (
    <Stop
      icon={<ShieldX className="size-7" aria-hidden />}
      eyebrow="Unauthorized"
      title={title}
      actions={
        <>
          <Button asChild>
            <a href="/">
              <ArrowLeft aria-hidden /> Back to the dashboard
            </a>
          </Button>
          <Button variant="secondary" onClick={() => window.history.back()}>
            Go back
          </Button>
        </>
      }
    >
      <p>
        This screen needs a permission this account does not hold. Ask an administrator if you
        need it — nothing has gone wrong with your session.
      </p>
    </Stop>
  );
}

/** What a route nobody has looks like. */
export function NotFoundPage() {
  return (
    <Stop
      icon={<SearchX className="size-7" aria-hidden />}
      eyebrow="404"
      title="That page isn't here"
      actions={
        <>
          <Button asChild>
            <a href="/">
              <ArrowLeft aria-hidden /> Back to the dashboard
            </a>
          </Button>
          <Button variant="secondary" onClick={() => window.history.back()}>
            Go back
          </Button>
        </>
      }
    >
      <p>
        The link may be out of date, or the page may need a permission this account does not
        have. Nothing has gone wrong with your session.
      </p>
    </Stop>
  );
}
